import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getMyDamageClaims, damageClaimStatusBadge } from '../../services/disputeService';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

export default function MyChargedClaims() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userProfile?.id) return;
    getMyDamageClaims(userProfile.id, 'rider')
      .then(setClaims)
      .catch((err) => toast.error(err.message || 'Could not load claims'))
      .finally(() => setLoading(false));
  }, [userProfile?.id]);

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Riders only see claims that have actually been actioned against them
  // (i.e. paid/charged, rejected — not draft submissions where they haven't been informed)
  const visibleClaims = claims.filter(c =>
    ['approved_paid', 'rejected', 'awaiting_charge', 'charge_failed'].includes(c.status)
  );

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/rider')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Dashboard</button>
      </div>

      <h1 className="font-serif mt-0 mb-2">Damage claims</h1>
      <p className="text-body text-[0.9rem] mb-4">
        Damage or cleaning claims involving your trips.
      </p>

      <div className="p-3 rounded-[10px] bg-slate-50 border border-slate-200 mb-4 text-[0.82rem] text-body">
        If you disagree with a claim, you can <button onClick={() => navigate('/rider/complaint/new')} className="bg-transparent border-0 p-0 text-brand-primary cursor-pointer underline font-semibold text-[inherit]">submit a complaint</button> to Reservo for review.
      </div>

      {loading && <div className="p-10 text-center text-muted">Loading...</div>}

      {!loading && visibleClaims.length === 0 && (
        <div className="p-10 text-center text-muted bg-white rounded-2xl border border-slate-200">
          No damage claims involve your trips.
        </div>
      )}

      <div className="grid gap-3">
        {visibleClaims.map(c => {
          const badge = damageClaimStatusBadge(c.status);
          return (
            <div key={c.id} onClick={() => navigate(`/rider/claims/${c.id}`)} className="p-4 rounded-2xl bg-white border border-slate-200 cursor-pointer transition-all">
              <div className="flex justify-between items-start mb-1.5">
                <div>
                  <div className="font-bold text-brand-dark">{c.referenceNumber}</div>
                  <div className="text-[0.78rem] text-muted capitalize">{c.type} claim</div>
                </div>
                <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
                  {badge.label}
                </span>
              </div>
              <div className="text-[0.85rem] text-body mb-2">
                {c.description?.substring(0, 100)}{c.description?.length > 100 ? '...' : ''}
              </div>
              <div className="flex justify-between text-[0.82rem]">
                <span className="text-body">
                  {c.status === 'approved_paid' && c.approvedAmount && (
                    <>Charged: <strong style={{ color: '#dc2626' }}>${c.approvedAmount.toFixed(2)}</strong></>
                  )}
                  {c.status === 'rejected' && <span style={{ color: '#059669' }}>No charge</span>}
                  {(c.status === 'awaiting_charge' || c.status === 'charge_failed') && c.approvedAmount && (
                    <>Pending: <strong>${c.approvedAmount.toFixed(2)}</strong></>
                  )}
                </span>
                <span className="text-muted">{fmtDate(c.submittedAt)}</span>
              </div>
            </div>
          );
        })}
      </div>
    <BackButtonBottom to="/rider" label="Dashboard" />
    </div>
  );
}